import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Broadcast, Users, Stop, UploadSimple, GearSix } from "@phosphor-icons/react";
import GoLiveModal from "../components/GoLiveModal";

export default function Live() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [streams, setStreams] = useState([]);
    const [goLiveOpen, setGoLiveOpen] = useState(false);
    const [ending, setEnding] = useState(false);
    const fileRef = useRef(null);

    const myLive = user ? streams.find((s) => s.user?.id === user.id) : null;

    const load = () => api.get("/live").then(({ data }) => setStreams(data));
    useEffect(() => { load(); }, []);

    const start = () => setGoLiveOpen(true);

    const onEndPickRecording = () => fileRef.current?.click();

    const onEndWithRecording = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!myLive) return;
        setEnding(true);
        try {
            let body = {};
            if (f) {
                if (!f.type.startsWith("video/")) { toast.error("Pick a video file"); setEnding(false); return; }
                if (f.size > 200 * 1024 * 1024) { toast.error("Recording max 200MB"); setEnding(false); return; }
                const form = new FormData();
                form.append("file", f);
                const { data: up } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
                body = { recording_url: `${process.env.REACT_APP_BACKEND_URL}${up.url}`, storage_path: up.path || "" };
            }
            await api.post(`/live/${myLive.id}/end`, body);
            toast.success(f ? "Stream ended — recording archived (auto-deletes in 48h)" : "Stream ended");
            await load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setEnding(false); }
    };

    const endNoRecording = async () => {
        if (!myLive) return;
        setEnding(true);
        try {
            await api.post(`/live/${myLive.id}/end`, {});
            toast.success("Stream ended");
            await load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setEnding(false); }
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-5xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="font-heading text-xs tracking-[0.4em] text-wta-accent">LIVE NOW</div>
                    <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">Live</h1>
                </div>
                {user && !myLive && (
                    <div className="flex items-center gap-2">
                        <Link to="/live-settings" data-testid="live-lobby-settings-link" className="btn-ghost px-3 py-2.5 rounded-sm text-sm flex items-center gap-2" title="Live Settings">
                            <GearSix size={16} weight="bold" /> Settings
                        </Link>
                        <button data-testid="go-live-btn" onClick={start} className="btn-primary px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
                            <Broadcast size={16} weight="fill" /> Go Live
                        </button>
                    </div>
                )}
                {user && myLive && (
                    <div className="flex items-center gap-2">
                        <input ref={fileRef} type="file" accept="video/*" onChange={onEndWithRecording} className="hidden" data-testid="end-recording-file" />
                        <Link to={`/live/${myLive.id}`} className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-2"><Broadcast size={14} weight="fill" /> Open my stream</Link>
                        <button data-testid="end-and-archive-btn" disabled={ending} onClick={onEndPickRecording} className="btn-ghost px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-50"><UploadSimple size={14} weight="bold" /> {ending ? "Saving…" : "End & Save"}</button>
                        <button data-testid="end-live-btn" disabled={ending} onClick={endNoRecording} className="btn-ghost px-4 py-2 rounded-sm text-sm font-heading uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"><Stop size={14} weight="fill" /> End</button>
                    </div>
                )}
            </div>
            <p className="text-wta-muted mt-2 max-w-xl">Need 500+ <span className="text-wta-primary">face-verified</span> followers to go live. Recordings stay in your archive for 48 hours, then auto-deleted.</p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                {streams.length === 0 ? (
                    <div className="col-span-full text-wta-muted text-center py-12">No live streams right now.</div>
                ) : streams.map((s) => (
                    <Link key={s.id} to={`/live/${s.id}`} data-testid={`live-card-${s.id}`} className="bg-wta-surface border border-wta-border rounded-sm overflow-hidden hover:border-wta-primary">
                        <div className="aspect-video bg-wta-surface2 relative flex items-center justify-center overflow-hidden">
                            {s.using_wallpaper && s.wallpaper_url ? (
                                <img src={s.wallpaper_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                            ) : null}
                            <div className="absolute top-3 left-3 px-2 py-1 bg-wta-primary text-white text-xs tracking-widest font-heading uppercase live-pulse rounded-sm">LIVE</div>
                            {!s.wallpaper_url && <Broadcast size={48} className="text-white/40 relative" weight="fill" />}
                            <div className="absolute bottom-3 right-3 glass px-2 py-1 rounded-sm text-xs flex items-center gap-1"><Users size={12} /> {s.viewers_count}</div>
                            {s.topic && <div className="absolute bottom-3 left-3 right-20 text-xs text-white truncate font-heading tracking-wide bg-black/50 px-2 py-1 rounded-sm">{s.topic}</div>}
                        </div>
                        <div className="p-3">
                            <div className="font-heading text-lg tracking-wide">@{s.user?.name}</div>
                            <div className="text-xs text-wta-muted">Started {new Date(s.started_at).toLocaleTimeString()}</div>
                        </div>
                    </Link>
                ))}
            </div>

            <GoLiveModal
                open={goLiveOpen}
                onClose={() => setGoLiveOpen(false)}
                onLive={(live) => { setGoLiveOpen(false); nav(`/live/${live.id}`); }}
            />
        </div>
    );
}
